import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Shield, Upload, FileCheck, Clock, CheckCircle, XCircle, AlertCircle, CreditCard, FileText
} from 'lucide-react';
import axios from 'axios';

const IdentityVerification = () => {
  const [documents, setDocuments] = useState([]);
  const [identityVerified, setIdentityVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/identity/my-documents`,
        { headers: getAuthHeader() }
      );
      setDocuments(response.data.documents || []);
      setIdentityVerified(response.data.identity_verified || false);
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!documentType) {
      alert('Please select a document type');
      return;
    }
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', documentType);

      await axios.post(
        `${API_URL}/api/identity/upload`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      alert('Document uploaded successfully! It will be reviewed by our team.');
      setSelectedFile(null);
      setDocumentType('');
      fetchDocuments();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      nic: 'National ID Card (NIC)',
      passport: 'Passport',
      driving_license: 'Driving License'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { className: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { className: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { className: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const { className, icon: Icon } = config[status] || config.pending;
    return (
      <Badge className={className}>
        <Icon className="h-3 w-3 mr-1" />
        {status.toUpperCase()}
      </Badge>
    );
  };

  const getDocumentIcon = (type) => {
    const icons = {
      nic: CreditCard,
      passport: FileText,
      driving_license: CreditCard
    };
    const Icon = icons[type] || FileCheck;
    return <Icon className="h-5 w-5" />;
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <Shield className="h-6 w-6" />
        Identity Verification
      </h1>

      {/* Verification Status */}
      <Card className={identityVerified ? 'bg-green-50 border-green-200 mb-6' : 'bg-blue-50 border-blue-200 mb-6'}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            {identityVerified ? (
              <>
                <CheckCircle className="h-10 w-10 text-green-500" />
                <div>
                  <h3 className="font-semibold text-green-800">Identity Verified</h3>
                  <p className="text-sm text-green-600">
                    Your identity has been verified. You now have access to all features.
                  </p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="h-10 w-10 text-blue-500" />
                <div>
                  <h3 className="font-semibold text-blue-800">Verify Your Identity</h3>
                  <p className="text-sm text-blue-600">
                    Upload a valid ID document to verify your identity and unlock all features.
                  </p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Upload Section */}
      {!identityVerified && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Document
            </CardTitle>
            <CardDescription>
              Upload a clear photo or scan of your identity document
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Document Type *</Label>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nic">National ID Card (NIC)</SelectItem>
                  <SelectItem value="passport">Passport</SelectItem>
                  <SelectItem value="driving_license">Driving License</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Upload File *</Label>
              <div className="mt-2">
                <Input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={handleFileChange}
                  className="cursor-pointer"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Accepted formats: JPG, PNG, PDF. Max size: 5MB
                </p>
              </div>
            </div>

            {selectedFile && (
              <div className="p-3 bg-gray-50 rounded-lg flex items-center gap-3">
                <FileCheck className="h-5 w-5 text-blue-500" />
                <span className="text-sm">{selectedFile.name}</span>
              </div>
            )}

            <Button
              onClick={handleUpload}
              disabled={uploading || !documentType || !selectedFile}
              className="w-full"
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle>Your Documents</CardTitle>
          <CardDescription>
            Track the status of your submitted documents
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-gray-500">Loading...</p>
          ) : documents.length === 0 ? (
            <p className="text-center py-8 text-gray-500">
              No documents submitted yet
            </p>
          ) : (
            <div className="space-y-4">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="border rounded-lg p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      {getDocumentIcon(doc.document_type)}
                    </div>
                    <div>
                      <p className="font-medium">{getDocumentTypeLabel(doc.document_type)}</p>
                      <p className="text-sm text-gray-500">
                        Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                      </p>
                      {doc.review_notes && doc.status === 'rejected' && (
                        <p className="text-sm text-red-500 mt-1">
                          Note: {doc.review_notes}
                        </p>
                      )}
                    </div>
                  </div>
                  {getStatusBadge(doc.status)}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Section */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Why Verify?</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Get a verified badge on your profile</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Build trust with potential buyers and sellers</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Access premium features and higher listing limits</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Priority customer support</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default IdentityVerification;
